import { useState, useRef } from 'react';
import axios from 'axios';

const API = process.env.REACT_APP_BACKEND_URL + '/api';

/**
 * Custom hook for address validation, autocomplete, and geocoding.
 */
export function useAddressValidation(formData, setFormData) {
  const [addressValidation, setAddressValidation] = useState(null);
  const [showAddressConfirm, setShowAddressConfirm] = useState(false);
  const [validatedAddress, setValidatedAddress] = useState(null);
  const [addressPredictions, setAddressPredictions] = useState([]);
  const [showAddressPredictions, setShowAddressPredictions] = useState(false);
  const [addressCoordinates, setAddressCoordinates] = useState(null);
  const addressDebounceTimer = useRef(null);

  const validateAddress = async () => {
    if (!formData.address || formData.address.trim().length < 5) {
      return true;
    }

    try {
      const response = await axios.get('https://nominatim.openstreetmap.org/search', {
        params: {
          q: formData.address,
          format: 'json',
          limit: 1,
          countrycodes: formData.country?.toLowerCase() || 'ch,de,at'
        },
        headers: { 'User-Agent': 'Erdwaermeprofis/1.0' }
      });

      if (response.data && response.data.length > 0) {
        const suggested = response.data[0];
        setValidatedAddress(suggested.display_name);

        const similarity = formData.address.toLowerCase().includes(suggested.display_name.toLowerCase().substring(0, 20)) ||
                          suggested.display_name.toLowerCase().includes(formData.address.toLowerCase().substring(0, 20));

        if (!similarity) {
          setShowAddressConfirm(true);
          return false;
        }
        return true;
      } else {
        setAddressValidation('Adresse konnte nicht gefunden werden. Bitte überprüfen Sie die Eingabe.');
        setShowAddressConfirm(true);
        return false;
      }
    } catch (error) {
      console.error('Address validation failed:', error);
      return true;
    }
  };

  const searchAddressPredictions = async (input) => {
    if (addressDebounceTimer.current) {
      clearTimeout(addressDebounceTimer.current);
    }

    if (!input || input.length < 3) {
      setAddressPredictions([]);
      setShowAddressPredictions(false);
      return;
    }

    addressDebounceTimer.current = setTimeout(async () => {
      try {
        console.log('Searching address predictions for:', input);

        const params = { input: input };

        if (formData.plz && formData.country) {
          params.plz = formData.plz;
          params.country = formData.country;
          console.log('Adding location bias:', formData.plz, formData.country);
        }

        const response = await axios.get(`${API}/google/places/autocomplete`, {
          params: params,
          timeout: 10000
        });

        console.log('Google Places via backend response:', response.data);

        if (response.data && response.data.predictions) {
          setAddressPredictions(response.data.predictions.slice(0, 5));
          setShowAddressPredictions(true);
        }
      } catch (error) {
        console.error('Google Places error via backend:', error);
        setAddressPredictions([]);
        setShowAddressPredictions(false);
      }
    }, 500);
  };

  const selectAddressPrediction = async (prediction) => {
    setFormData(prev => ({ ...prev, address: prediction.description }));
    setShowAddressPredictions(false);
    setAddressPredictions([]);

    try {
      console.log('Getting place details for place_id:', prediction.place_id);
      const placeDetailsResponse = await axios.get(`${API}/google/places/details`, {
        params: { place_id: prediction.place_id },
        timeout: 10000
      });

      console.log('Place details response:', placeDetailsResponse.data);

      if (placeDetailsResponse.data && placeDetailsResponse.data.result) {
        const result = placeDetailsResponse.data.result;

        if (result.geometry && result.geometry.location) {
          const location = result.geometry.location;
          setAddressCoordinates({
            lat: location.lat,
            lng: location.lng,
            address: result.formatted_address || prediction.description
          });
          console.log('Precise address coordinates set from place_id:', location);

          if (result.formatted_address) {
            setFormData(prev => ({ ...prev, address: result.formatted_address }));
          }
        }
      }
    } catch (error) {
      console.error('Failed to get place details:', error);

      try {
        console.log('Fallback: Using geocoding for:', prediction.description);
        const geocodeResponse = await axios.get(`${API}/google/geocode`, {
          params: { address: prediction.description },
          timeout: 10000
        });

        if (geocodeResponse.data && geocodeResponse.data.results && geocodeResponse.data.results.length > 0) {
          const location = geocodeResponse.data.results[0].geometry.location;
          setAddressCoordinates({
            lat: location.lat,
            lng: location.lng,
            address: prediction.description
          });
          console.log('Fallback geocoding successful:', location);
        }
      } catch (fallbackError) {
        console.error('Fallback geocoding also failed:', fallbackError);
      }
    }
  };

  return {
    addressValidation,
    showAddressConfirm,
    validatedAddress,
    addressPredictions,
    showAddressPredictions,
    addressCoordinates,
    validateAddress,
    searchAddressPredictions,
    selectAddressPrediction,
    setShowAddressConfirm,
    setAddressValidation,
  };
}
